import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CircleDollarSign,
  ClipboardList,
  Loader2,
  PencilLine,
  Plus,
  Search,
  ShieldAlert,
  UserRound,
  UsersRound,
  WalletCards,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FinanceLockGate from '../components/FinanceLockGate';
import useAuthStore from '../store/useAuthStore';
import { hasFinanceRole } from '../utils/rolePermissions';
import {
  addConsultantAdjustment,
  addConsultantPayment,
  archiveConsultant,
  createConsultant,
  createConsultantFeeRecord,
  getConsultantLedgerSummary,
  getConsultantWiseLedger,
  getConsultants,
  getInvoices,
  getPatientWiseLedger,
  searchPatients,
  updateConsultant,
} from '../services/consultantLedgerApi';

const RUPEE_SYMBOL = '\u20B9';
const FEE_STATUS_OPTIONS = ['unpaid', 'partially_paid', 'fully_paid', 'adjusted', 'cancelled'];
const PAGE_SIZE = 50;

function formatCurrency(value) {
  return `${RUPEE_SYMBOL}${Number(value || 0).toFixed(2)}`;
}

function statusLabel(value) {
  if (!value) return '-';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function isValidPositiveNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function getFriendlyError(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const backendError = error?.response?.data?.error;

  if (status === 423 || code === 'FINANCE_LOCK_REQUIRED') {
    return 'Finance data is locked. Unlock Finance Lock and try again.';
  }
  if (status === 403) {
    return backendError || 'You do not have permission for this action.';
  }
  if (status === 404) {
    return backendError || 'Record not found.';
  }
  if (status === 409) {
    return backendError || 'This record is locked and cannot be changed directly.';
  }
  if (status === 400) {
    return backendError || 'Please check entered values and try again.';
  }

  return backendError || error?.message || 'Something went wrong. Please try again.';
}

function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-2 sm:p-4">
      <div className="flex max-h-[calc(100dvh-1rem)] w-full max-w-2xl flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.28)] sm:max-h-[calc(100dvh-2rem)]">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function ConsultantFeesLedger() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const canUseFinance = hasFinanceRole(user?.role);
  const [initializing, setInitializing] = useState(true);
  const [activeView, setActiveView] = useState('consultant');
  const [summary, setSummary] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [consultantWiseRows, setConsultantWiseRows] = useState([]);
  const [patientWisePayload, setPatientWisePayload] = useState({ count: 0, rows: [], limit: PAGE_SIZE, offset: 0 });
  const [invoices, setInvoices] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [savingAction, setSavingAction] = useState('');
  const savingActionRef = useRef('');
  const [successNotice, setSuccessNotice] = useState('');
  const [consultantSaveError, setConsultantSaveError] = useState('');
  const [feeRecordSaveError, setFeeRecordSaveError] = useState('');

  const [consultantFilters, setConsultantFilters] = useState({
    consultantId: '',
    status: '',
    startDate: '',
    endDate: '',
    specialization: '',
  });
  const [patientFilters, setPatientFilters] = useState({
    consultantId: '',
    status: '',
    startDate: '',
    endDate: '',
    limit: PAGE_SIZE,
    offset: 0,
  });

  const [consultantModal, setConsultantModal] = useState({
    open: false,
    mode: 'create',
    consultantId: null,
    consultantName: '',
    specialization: '',
    phone: '',
    email: '',
    upiId: '',
    paymentDetails: '',
    notes: '',
  });

  const [feeRecordModal, setFeeRecordModal] = useState({
    open: false,
    consultantId: '',
    patientSearch: '',
    selectedPatient: null,
    invoiceId: '',
    treatmentNameSnapshot: '',
    agreedConsultantFee: '',
    dueDate: '',
    notes: '',
    patientSearchResults: [],
    searchingPatient: false,
  });

  const [paymentModal, setPaymentModal] = useState({
    open: false,
    record: null,
    amountPaid: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: '',
    paymentNotes: '',
  });

  const [adjustmentModal, setAdjustmentModal] = useState({
    open: false,
    record: null,
    adjustmentType: 'increase',
    adjustmentAmount: '',
    reason: '',
  });

  const loadSummary = useCallback(async () => {
    const data = await getConsultantLedgerSummary();
    setSummary(data || null);
  }, []);

  const loadConsultants = useCallback(async () => {
    const rows = await getConsultants({ includeArchived: false });
    setConsultants(Array.isArray(rows) ? rows : []);
  }, []);

  const loadInvoices = useCallback(async () => {
    const rows = await getInvoices();
    setInvoices(Array.isArray(rows) ? rows : []);
  }, []);

  const loadConsultantWise = useCallback(async () => {
    setLoadingRows(true);
    try {
      const payload = await getConsultantWiseLedger({
        consultantId: consultantFilters.consultantId || undefined,
        status: consultantFilters.status || undefined,
        startDate: consultantFilters.startDate || undefined,
        endDate: consultantFilters.endDate || undefined,
      });
      const rows = Array.isArray(payload) ? payload : [];
      const specializationFilter = String(consultantFilters.specialization || '').trim().toLowerCase();
      const filteredRows = specializationFilter
        ? rows.filter((row) =>
            String(row.specialization || '')
              .toLowerCase()
              .includes(specializationFilter)
          )
        : rows;
      setConsultantWiseRows(filteredRows);
    } finally {
      setLoadingRows(false);
    }
  }, [consultantFilters]);

  const loadPatientWise = useCallback(async () => {
    setLoadingRows(true);
    try {
      const payload = await getPatientWiseLedger({
        consultantId: patientFilters.consultantId || undefined,
        status: patientFilters.status || undefined,
        startDate: patientFilters.startDate || undefined,
        endDate: patientFilters.endDate || undefined,
        limit: patientFilters.limit,
        offset: patientFilters.offset,
      });
      setPatientWisePayload({
        count: Number(payload?.count || 0),
        rows: Array.isArray(payload?.rows) ? payload.rows : [],
        limit: Number(payload?.limit || patientFilters.limit),
        offset: Number(payload?.offset || patientFilters.offset),
      });
    } finally {
      setLoadingRows(false);
    }
  }, [patientFilters]);

  const loadAll = useCallback(async () => {
    setInitializing(true);
    try {
      await Promise.all([loadSummary(), loadConsultants(), loadInvoices(), loadConsultantWise(), loadPatientWise()]);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setInitializing(false);
    }
  }, [loadConsultantWise, loadConsultants, loadInvoices, loadPatientWise, loadSummary]);

  useEffect(() => {
    if (!initializing) {
      loadConsultantWise().catch((error) => toast.error(getFriendlyError(error)));
    }
  }, [consultantFilters, initializing, loadConsultantWise]);

  useEffect(() => {
    if (!initializing) {
      loadPatientWise().catch((error) => toast.error(getFriendlyError(error)));
    }
  }, [patientFilters, initializing, loadPatientWise]);

  const summaryCards = useMemo(() => {
    const payable = Number(summary?.totalAgreedFees || 0);
    const paid = Number(summary?.totalPaid || 0);
    const balance = Number(summary?.totalBalanceRemaining || 0);
    const pendingConsultants = consultantWiseRows.filter((row) => Number(row.balanceRemaining || 0) > 0).length;
    const fullyPaidRecords = Number(summary?.statusBreakdown?.fully_paid || 0);

    return [
      { label: 'Total Payable', value: formatCurrency(payable), icon: WalletCards },
      { label: 'Total Paid', value: formatCurrency(paid), icon: CircleDollarSign },
      { label: 'Total Balance', value: formatCurrency(balance), icon: AlertTriangle },
      { label: 'Pending Consultants', value: String(pendingConsultants), icon: UsersRound },
      { label: 'Fully Paid Records', value: String(fullyPaidRecords), icon: ClipboardList },
    ];
  }, [consultantWiseRows, summary]);

  const paymentSnapshotRows = useMemo(() => {
    return (patientWisePayload.rows || []).map((row) => ({
      id: row.id,
      patientName: row?.Patient?.name || '-',
      consultantName: row?.Consultant?.consultantName || '-',
      dueDate: row?.dueDate || '',
      totalPaid: Number(row?.totalPaid || 0),
      balanceRemaining: Number(row?.balanceRemaining || 0),
      status: row?.status || '',
    }));
  }, [patientWisePayload.rows]);

  const invoiceOptionsForFeeModal = useMemo(() => {
    const selectedPatientId = Number(feeRecordModal.selectedPatient?.id || 0);
    if (!selectedPatientId) return invoices.slice(0, 100);
    return invoices.filter((item) => Number(item?.patientId) === selectedPatientId).slice(0, 100);
  }, [feeRecordModal.selectedPatient?.id, invoices]);

  const openCreateConsultantModal = () => {
    setConsultantSaveError('');
    setConsultantModal({
      open: true,
      mode: 'create',
      consultantId: null,
      consultantName: '',
      specialization: '',
      phone: '',
      email: '',
      upiId: '',
      paymentDetails: '',
      notes: '',
    });
  };

  const openEditConsultantModal = (row) => {
    setConsultantSaveError('');
    setConsultantModal({
      open: true,
      mode: 'edit',
      consultantId: row.id,
      consultantName: row.consultantName || '',
      specialization: row.specialization || '',
      phone: row.phone || '',
      email: row.email || '',
      upiId: row.upiId || '',
      paymentDetails: row.paymentDetails || '',
      notes: row.notes || '',
    });
  };

  const saveConsultant = async () => {
    if (savingActionRef.current) return;
    if (!String(consultantModal.consultantName || '').trim()) {
      toast.error('Consultant Name is required.');
      return;
    }

    savingActionRef.current = 'consultant';
    setSavingAction('consultant');
    setConsultantSaveError('');
    try {
      const payload = {
        consultantName: consultantModal.consultantName,
        specialization: consultantModal.specialization,
        phone: consultantModal.phone,
        email: consultantModal.email,
        upiId: consultantModal.upiId,
        paymentDetails: consultantModal.paymentDetails,
        notes: consultantModal.notes,
      };

      if (consultantModal.mode === 'edit' && consultantModal.consultantId) {
        await updateConsultant(consultantModal.consultantId, payload);
        await Promise.all([loadConsultants(), loadConsultantWise(), loadSummary()]);
        setSuccessNotice('Consultant updated successfully.');
        toast.success('Consultant updated successfully.');
      } else {
        await createConsultant(payload);
        await Promise.all([loadConsultants(), loadConsultantWise(), loadSummary()]);
        setSuccessNotice('Visiting consultant saved successfully and is now available for fee records.');
        toast.success('Visiting consultant saved successfully.');
      }

      setConsultantModal((prev) => ({ ...prev, open: false }));
    } catch (error) {
      const message = getFriendlyError(error);
      setConsultantSaveError(message);
      toast.error(message);
    } finally {
      savingActionRef.current = '';
      setSavingAction('');
    }
  };

  const archiveConsultantRow = async (row) => {
    const confirmation = window.confirm(`Archive consultant "${row.consultantName}"?`);
    if (!confirmation) return;

    setSavingAction(`archive-${row.id}`);
    try {
      await archiveConsultant(row.id, 'Archived from Consultant Ledger frontend');
      toast.success('Consultant archived.');
      await Promise.all([loadConsultants(), loadConsultantWise(), loadSummary()]);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const searchFeeModalPatients = async () => {
    const query = String(feeRecordModal.patientSearch || '').trim();
    if (query.length < 2) {
      toast.error('Enter at least 2 characters to search patient.');
      return;
    }

    setFeeRecordModal((prev) => ({ ...prev, searchingPatient: true }));
    try {
      const rows = await searchPatients(query);
      setFeeRecordModal((prev) => ({
        ...prev,
        searchingPatient: false,
        patientSearchResults: Array.isArray(rows) ? rows.slice(0, 20) : [],
      }));
    } catch (error) {
      setFeeRecordModal((prev) => ({ ...prev, searchingPatient: false, patientSearchResults: [] }));
      toast.error(getFriendlyError(error));
    }
  };

  const saveFeeRecord = async () => {
    if (savingActionRef.current) return;
    if (!feeRecordModal.consultantId) {
      toast.error('Consultant is required.');
      return;
    }
    if (!feeRecordModal.selectedPatient?.id) {
      toast.error('Patient is required.');
      return;
    }
    if (!isValidPositiveNumber(feeRecordModal.agreedConsultantFee) && Number(feeRecordModal.agreedConsultantFee) !== 0) {
      toast.error('Agreed Consultant Fee must be zero or greater.');
      return;
    }
    if (Number(feeRecordModal.agreedConsultantFee) < 0) {
      toast.error('Agreed Consultant Fee cannot be negative.');
      return;
    }

    savingActionRef.current = 'fee-record';
    setSavingAction('fee-record');
    setFeeRecordSaveError('');
    try {
      await createConsultantFeeRecord({
        consultantId: Number(feeRecordModal.consultantId),
        patientId: Number(feeRecordModal.selectedPatient.id),
        invoiceId: feeRecordModal.invoiceId ? Number(feeRecordModal.invoiceId) : null,
        treatmentNameSnapshot: feeRecordModal.treatmentNameSnapshot || null,
        agreedConsultantFee: Number(feeRecordModal.agreedConsultantFee || 0),
        dueDate: feeRecordModal.dueDate || null,
        notes: feeRecordModal.notes || null,
      });
      await Promise.all([loadPatientWise(), loadConsultantWise(), loadSummary()]);
      setSuccessNotice('Consultant fee record saved successfully. The ledger and totals are up to date.');
      toast.success('Consultant fee record saved successfully.');
      setFeeRecordModal({
        open: false,
        consultantId: '',
        patientSearch: '',
        selectedPatient: null,
        invoiceId: '',
        treatmentNameSnapshot: '',
        agreedConsultantFee: '',
        dueDate: '',
        notes: '',
        patientSearchResults: [],
        searchingPatient: false,
      });
    } catch (error) {
      const message = getFriendlyError(error);
      setFeeRecordSaveError(message);
      toast.error(message);
    } finally {
      savingActionRef.current = '';
      setSavingAction('');
    }
  };

  const openPaymentForRecord = (record) => {
    setPaymentModal({
      open: true,
      record,
      amountPaid: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: '',
      paymentNotes: '',
    });
  };

  const savePayment = async () => {
    const record = paymentModal.record;
    if (!record?.id) {
      toast.error('Select a valid fee record.');
      return;
    }
    if (!isValidPositiveNumber(paymentModal.amountPaid)) {
      toast.error('Amount Paid must be a positive number.');
      return;
    }
    if (!paymentModal.paymentDate) {
      toast.error('Payment Date is required.');
      return;
    }

    setSavingAction('payment');
    try {
      await addConsultantPayment({
        consultantFeeRecordId: Number(record.id),
        amountPaid: Number(paymentModal.amountPaid),
        paymentDate: paymentModal.paymentDate,
        paymentMode: paymentModal.paymentMode || null,
        paymentNotes: paymentModal.paymentNotes || null,
      });
      toast.success('Consultant payment added.');
      setPaymentModal({
        open: false,
        record: null,
        amountPaid: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMode: '',
        paymentNotes: '',
      });
      await Promise.all([loadPatientWise(), loadConsultantWise(), loadSummary()]);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const openAdjustmentForRecord = (record) => {
    setAdjustmentModal({
      open: true,
      record,
      adjustmentType: 'increase',
      adjustmentAmount: '',
      reason: '',
    });
  };

  const saveAdjustment = async () => {
    const record = adjustmentModal.record;
    if (!record?.id) {
      toast.error('Select a valid fee record.');
      return;
    }
    if (!isValidPositiveNumber(adjustmentModal.adjustmentAmount)) {
      toast.error('Adjustment Amount must be a positive number.');
      return;
    }
    if (!String(adjustmentModal.reason || '').trim()) {
      toast.error('Reason is required for adjustment.');
      return;
    }

    setSavingAction('adjustment');
    try {
      await addConsultantAdjustment({
        consultantFeeRecordId: Number(record.id),
        adjustmentType: adjustmentModal.adjustmentType,
        adjustmentAmount: Number(adjustmentModal.adjustmentAmount),
        reason: adjustmentModal.reason.trim(),
      });
      toast.success('Consultant adjustment added.');
      setAdjustmentModal({
        open: false,
        record: null,
        adjustmentType: 'increase',
        adjustmentAmount: '',
        reason: '',
      });
      await Promise.all([loadPatientWise(), loadConsultantWise(), loadSummary()]);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setSavingAction('');
    }
  };

  const viewConsultantDetails = (consultantId) => {
    setPatientFilters((prev) => ({
      ...prev,
      consultantId: String(consultantId),
      offset: 0,
    }));
    setActiveView('patient');
  };

  const nextPatientPage = () => {
    const nextOffset = patientWisePayload.offset + patientWisePayload.limit;
    if (nextOffset >= patientWisePayload.count) return;
    setPatientFilters((prev) => ({ ...prev, offset: nextOffset }));
  };

  const prevPatientPage = () => {
    const nextOffset = Math.max(0, patientWisePayload.offset - patientWisePayload.limit);
    setPatientFilters((prev) => ({ ...prev, offset: nextOffset }));
  };

  return (
    <FinanceLockGate
      title="Finance Lock enabled. Enter PIN to continue."
      description="Consultant Ledger contains agreed fees, payments, balances, and adjustment history."
      onAccessGranted={loadAll}
    >
      {initializing ? (
        <div className="section-card p-6 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
            Loading Consultant Fees Ledger...
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="page-title">Consultant Payments</h1>
              <p className="page-subtitle">
                Track consultant dues, partial payments, and patient-wise balances.
              </p>
              <details className="mt-3 max-w-2xl rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <summary className="cursor-pointer font-semibold text-slate-800">How Consultant Payments works</summary>
                <p className="mt-2">
                  Add the visiting specialists who work at your clinic. Record what you owe them for each case and track pending or completed payments.
                </p>
              </details>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/reports')}
              >
                <ClipboardList className="h-4 w-4" />
                Back to Business Reports
              </button>
              {canUseFinance ? (
                <>
                  <button type="button" className="btn-secondary" onClick={openCreateConsultantModal}>
                    <UsersRound className="h-4 w-4" />
                    Add Visiting Consultant
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() =>
                      {
                        setFeeRecordSaveError('');
                        setFeeRecordModal((prev) => ({
                          ...prev,
                          open: true,
                        }));
                      }
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Record Consultant Fee
                  </button>
                </>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                  <ShieldAlert className="h-4 w-4" />
                  Finance actions are restricted for your role.
                </div>
              )}
            </div>
          </div>

          {successNotice ? (
            <div
              role="status"
              aria-live="polite"
              className="flex items-start justify-between gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 shadow-sm"
            >
              <span>{successNotice}</span>
              <button
                type="button"
                aria-label="Dismiss success message"
                className="shrink-0 rounded-full p-1 text-emerald-700 hover:bg-emerald-100"
                onClick={() => setSuccessNotice('')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            {summaryCards.map((item) => (
              <div key={item.label} className="metric-card">
                <div className="flex items-center justify-between gap-3">
                  <p className="metric-label">{item.label}</p>
                  <item.icon className="h-4 w-4 text-indigo-600" />
                </div>
                <p className="metric-value text-[24px]">{item.value}</p>
              </div>
            ))}
          </div>

          <section className="section-card p-5" aria-labelledby="visiting-consultants-heading">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 id="visiting-consultants-heading" className="text-lg font-semibold text-slate-900">
                  Visiting Consultants
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {consultants.length} {consultants.length === 1 ? 'consultant' : 'consultants'} available for fee records.
                </p>
              </div>
            </div>
            {consultants.length === 0 ? (
              <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                No visiting consultants added yet. Add one to start recording consultant fees.
              </p>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {consultants.map((consultant) => (
                  <div key={consultant.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="font-semibold text-slate-900">{consultant.consultantName}</p>
                    <p className="mt-1 text-sm text-slate-500">{consultant.specialization || 'Specialisation not provided'}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="section-card p-5">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={activeView === 'consultant' ? 'btn-tab-active' : 'btn-tab'}
                onClick={() => setActiveView('consultant')}
              >
                Consultant-wise View
              </button>
              <button
                type="button"
                className={activeView === 'patient' ? 'btn-tab-active' : 'btn-tab'}
                onClick={() => setActiveView('patient')}
              >
                Patient-wise View
              </button>
              <button
                type="button"
                className={activeView === 'payments' ? 'btn-tab-active' : 'btn-tab'}
                onClick={() => setActiveView('payments')}
              >
                Payments / History
              </button>
            </div>
          </div>

          {activeView === 'consultant' ? (
            <section className="section-card p-5">
              <h2 className="text-lg font-semibold text-slate-900">Consultant-wise Ledger</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
                <select
                  value={consultantFilters.consultantId}
                  onChange={(event) =>
                    setConsultantFilters((prev) => ({ ...prev, consultantId: event.target.value }))
                  }
                  className="input-modern"
                >
                  <option value="">All Consultants</option>
                  {consultants.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.consultantName}
                    </option>
                  ))}
                </select>
                <input
                  value={consultantFilters.specialization}
                  onChange={(event) =>
                    setConsultantFilters((prev) => ({ ...prev, specialization: event.target.value }))
                  }
                  placeholder="Specialization"
                  className="input-modern"
                />
                <select
                  value={consultantFilters.status}
                  onChange={(event) =>
                    setConsultantFilters((prev) => ({ ...prev, status: event.target.value }))
                  }
                  className="input-modern"
                >
                  <option value="">All Status</option>
                  {FEE_STATUS_OPTIONS.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {statusLabel(statusOption)}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={consultantFilters.startDate}
                  onChange={(event) =>
                    setConsultantFilters((prev) => ({ ...prev, startDate: event.target.value }))
                  }
                  className="input-modern"
                />
                <input
                  type="date"
                  value={consultantFilters.endDate}
                  onChange={(event) =>
                    setConsultantFilters((prev) => ({ ...prev, endDate: event.target.value }))
                  }
                  className="input-modern"
                />
              </div>

              <div className="mt-4 table-shell">
                <div className="overflow-x-auto">
                  <table className="table-modern">
                    <thead>
                      <tr>
                        <th>Consultant</th>
                        <th>Specialization</th>
                        <th>Total Patients</th>
                        <th>Total Agreed Fees</th>
                        <th>Total Paid</th>
                        <th>Balance Remaining</th>
                        <th>Due Items</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingRows ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="flex items-center gap-2 text-slate-500">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading consultant-wise records...
                            </div>
                          </td>
                        </tr>
                      ) : consultantWiseRows.length === 0 ? (
                        <tr>
                          <td colSpan={8}>No consultant-wise records found for current filters.</td>
                        </tr>
                      ) : (
                        consultantWiseRows.map((row) => (
                          <tr key={row.consultantId}>
                            <td className="font-semibold text-slate-900">{row.consultantName || '-'}</td>
                            <td>{row.specialization || '-'}</td>
                            <td>{row.totalPatientsServed || 0}</td>
                            <td>{formatCurrency(row.totalAgreedFees)}</td>
                            <td>{formatCurrency(row.totalPaid)}</td>
                            <td>{formatCurrency(row.balanceRemaining)}</td>
                            <td>
                              <span className="pill-status pill-yellow">{row.dueItems || 0}</span>
                            </td>
                            <td>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn-secondary !rounded-xl !px-3 !py-1.5 !text-xs"
                                  onClick={() => viewConsultantDetails(row.consultantId)}
                                >
                                  View Details
                                </button>
                                {canUseFinance ? (
                                  <>
                                    <button
                                      type="button"
                                      className="btn-secondary !rounded-xl !px-3 !py-1.5 !text-xs"
                                      onClick={() => {
                                        const editableRow = consultants.find((item) => Number(item.id) === Number(row.consultantId));
                                        if (editableRow) openEditConsultantModal(editableRow);
                                      }}
                                    >
                                      <PencilLine className="h-3.5 w-3.5" />
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-secondary !rounded-xl !px-3 !py-1.5 !text-xs !border-rose-200 !text-rose-700"
                                      disabled={savingAction === `archive-${row.consultantId}`}
                                      onClick={() => {
                                        const editableRow = consultants.find((item) => Number(item.id) === Number(row.consultantId));
                                        if (editableRow) archiveConsultantRow(editableRow);
                                      }}
                                    >
                                      Archive
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : null}

          {activeView === 'patient' ? (
            <section className="section-card p-5">
              <h2 className="text-lg font-semibold text-slate-900">Patient-wise Consultant Ledger</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
                <select
                  value={patientFilters.consultantId}
                  onChange={(event) =>
                    setPatientFilters((prev) => ({ ...prev, consultantId: event.target.value, offset: 0 }))
                  }
                  className="input-modern"
                >
                  <option value="">All Consultants</option>
                  {consultants.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.consultantName}
                    </option>
                  ))}
                </select>
                <select
                  value={patientFilters.status}
                  onChange={(event) =>
                    setPatientFilters((prev) => ({ ...prev, status: event.target.value, offset: 0 }))
                  }
                  className="input-modern"
                >
                  <option value="">All Status</option>
                  {FEE_STATUS_OPTIONS.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {statusLabel(statusOption)}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={patientFilters.startDate}
                  onChange={(event) =>
                    setPatientFilters((prev) => ({ ...prev, startDate: event.target.value, offset: 0 }))
                  }
                  className="input-modern"
                />
                <input
                  type="date"
                  value={patientFilters.endDate}
                  onChange={(event) =>
                    setPatientFilters((prev) => ({ ...prev, endDate: event.target.value, offset: 0 }))
                  }
                  className="input-modern"
                />
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <span>
                    Showing {Math.min(patientWisePayload.count, patientWisePayload.offset + 1)}-
                    {Math.min(patientWisePayload.count, patientWisePayload.offset + patientWisePayload.limit)}
                  </span>
                  <span>of {patientWisePayload.count}</span>
                </div>
              </div>

              <div className="mt-4 table-shell">
                <div className="overflow-x-auto">
                  <table className="table-modern">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th>Consultant</th>
                        <th>Treatment / Service</th>
                        <th>Invoice Ref</th>
                        <th>Agreed Fee</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingRows ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="flex items-center gap-2 text-slate-500">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading patient-wise records...
                            </div>
                          </td>
                        </tr>
                      ) : patientWisePayload.rows.length === 0 ? (
                        <tr>
                          <td colSpan={9}>No patient-wise consultant records found for current filters.</td>
                        </tr>
                      ) : (
                        patientWisePayload.rows.map((row) => {
                          const recordLocked = row.status === 'fully_paid' || row.status === 'cancelled' || Number(row.balanceRemaining || 0) <= 0;
                          return (
                            <tr key={row.id}>
                              <td className="font-semibold text-slate-900">{row?.Patient?.name || '-'}</td>
                              <td>{row?.Consultant?.consultantName || '-'}</td>
                              <td>{row.treatmentNameSnapshot || '-'}</td>
                              <td>{row?.Invoice?.invoiceNumber || '-'}</td>
                              <td>{formatCurrency(row.agreedConsultantFee)}</td>
                              <td>{formatCurrency(row.totalPaid)}</td>
                              <td>{formatCurrency(row.balanceRemaining)}</td>
                              <td>
                                <span className={`pill-status ${row.status === 'fully_paid' ? 'pill-green' : row.status === 'partially_paid' ? 'pill-yellow' : 'pill-red'}`}>
                                  {statusLabel(row.status)}
                                </span>
                              </td>
                              <td>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className="btn-secondary !rounded-xl !px-3 !py-1.5 !text-xs"
                                    disabled={recordLocked}
                                    onClick={() => openPaymentForRecord(row)}
                                  >
                                    Add Payment
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary !rounded-xl !px-3 !py-1.5 !text-xs"
                                    disabled={row.status === 'cancelled'}
                                    onClick={() => openAdjustmentForRecord(row)}
                                  >
                                    Add Adjustment
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary !rounded-xl !px-3 !py-1.5 !text-xs"
                                    onClick={() => {
                                      const patientId = row?.Patient?.id || row?.patientId;
                                      navigate(patientId ? `/patients?patientId=${encodeURIComponent(patientId)}` : '/patients', {
                                        state: patientId ? { patientId } : undefined,
                                      });
                                    }}
                                  >
                                    View Patient
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs"
                  onClick={prevPatientPage}
                  disabled={patientWisePayload.offset <= 0}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn-secondary !rounded-xl !px-3 !py-2 !text-xs"
                  onClick={nextPatientPage}
                  disabled={patientWisePayload.offset + patientWisePayload.limit >= patientWisePayload.count}
                >
                  Next
                </button>
              </div>
            </section>
          ) : null}

          {activeView === 'payments' ? (
            <section className="section-card p-5">
              <h2 className="text-lg font-semibold text-slate-900">Payments / History Snapshot</h2>
              <p className="mt-1 text-sm text-slate-500">
                This view summarizes payment progress per patient-linked consultant entry.
              </p>

              <div className="mt-4 table-shell">
                <div className="overflow-x-auto">
                  <table className="table-modern">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th>Consultant</th>
                        <th>Due Date</th>
                        <th>Paid Amount</th>
                        <th>Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentSnapshotRows.length === 0 ? (
                        <tr>
                          <td colSpan={6}>No payment snapshot rows available.</td>
                        </tr>
                      ) : (
                        paymentSnapshotRows.map((row) => (
                          <tr key={row.id}>
                            <td className="font-semibold text-slate-900">{row.patientName}</td>
                            <td>{row.consultantName}</td>
                            <td>{row.dueDate || '-'}</td>
                            <td>{formatCurrency(row.totalPaid)}</td>
                            <td>{formatCurrency(row.balanceRemaining)}</td>
                            <td>
                              <span className={`pill-status ${row.status === 'fully_paid' ? 'pill-green' : row.status === 'partially_paid' ? 'pill-yellow' : 'pill-red'}`}>
                                {statusLabel(row.status)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : null}

          {consultantModal.open ? (
            <ModalShell
              title={consultantModal.mode === 'edit' ? 'Edit Visiting Consultant' : 'Add Visiting Consultant'}
              onClose={() => setConsultantModal((prev) => ({ ...prev, open: false }))}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="label-modern">Consultant name *</label>
                  <input
                    className="input-modern"
                    value={consultantModal.consultantName}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, consultantName: event.target.value }))}
                    placeholder="Consultant Name"
                  />
                </div>
                <div>
                  <label className="label-modern">Specialisation</label>
                  <input
                    className="input-modern"
                    value={consultantModal.specialization}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, specialization: event.target.value }))}
                    placeholder="Specialization"
                  />
                </div>
                <div>
                  <label className="label-modern">Phone</label>
                  <input
                    className="input-modern"
                    value={consultantModal.phone}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, phone: event.target.value }))}
                    placeholder="Phone"
                  />
                </div>
                <div>
                  <label className="label-modern">Email (optional)</label>
                  <input
                    className="input-modern"
                    value={consultantModal.email}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, email: event.target.value }))}
                    placeholder="Email"
                  />
                </div>
                <div>
                  <label className="label-modern">UPI ID (optional)</label>
                  <input
                    className="input-modern"
                    value={consultantModal.upiId}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, upiId: event.target.value }))}
                    placeholder="UPI ID"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label-modern">Payment notes (optional)</label>
                  <textarea
                    className="textarea-modern"
                    value={consultantModal.paymentDetails}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, paymentDetails: event.target.value }))}
                    placeholder="Payment Details"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label-modern">Internal notes (optional)</label>
                  <textarea
                    className="textarea-modern"
                    value={consultantModal.notes}
                    onChange={(event) => setConsultantModal((prev) => ({ ...prev, notes: event.target.value }))}
                    placeholder="Notes"
                  />
                </div>
              </div>
              {consultantSaveError ? (
                <div role="alert" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {consultantSaveError}
                </div>
              ) : null}
              <div
                data-testid="consultant-modal-actions"
                className="sticky bottom-0 z-10 -mx-5 mt-5 flex justify-end gap-2 border-t border-slate-100 bg-white px-5 pb-[env(safe-area-inset-bottom)] pt-4 shadow-[0_-10px_24px_rgba(255,255,255,0.96)]"
              >
                <button type="button" className="btn-secondary" onClick={() => setConsultantModal((prev) => ({ ...prev, open: false }))}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={saveConsultant} disabled={savingAction === 'consultant'}>
                  {savingAction === 'consultant' ? 'Saving...' : 'Save Consultant'}
                </button>
              </div>
            </ModalShell>
          ) : null}

          {feeRecordModal.open ? (
            <ModalShell
              title="Record Consultant Fee"
              onClose={() => setFeeRecordModal((prev) => ({ ...prev, open: false }))}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="label-modern">Select consultant *</label>
                  <select
                    className="input-modern"
                    value={feeRecordModal.consultantId}
                    onChange={(event) => setFeeRecordModal((prev) => ({ ...prev, consultantId: event.target.value }))}
                  >
                    <option value="">Select Consultant</option>
                    {consultants.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.consultantName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="label-modern">Find patient *</label>
                  <div className="flex gap-2">
                    <input
                      className="input-modern"
                      value={feeRecordModal.patientSearch}
                      onChange={(event) =>
                        setFeeRecordModal((prev) => ({ ...prev, patientSearch: event.target.value }))
                      }
                      placeholder="Search patient by name / phone / ID"
                    />
                    <button type="button" className="btn-secondary" onClick={searchFeeModalPatients} disabled={feeRecordModal.searchingPatient}>
                      {feeRecordModal.searchingPatient ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      Search
                    </button>
                  </div>

                  {feeRecordModal.patientSearchResults.length > 0 ? (
                    <div className="mt-3 max-h-40 overflow-auto rounded-2xl border border-slate-200 bg-slate-50">
                      {feeRecordModal.patientSearchResults.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() =>
                            setFeeRecordModal((prev) => ({
                              ...prev,
                              selectedPatient: item,
                              patientSearchResults: [],
                              patientSearch: `${item.name} (${item.patientId || item.phone || '-'})`,
                            }))
                          }
                          className="flex w-full items-center justify-between border-b border-slate-200/70 px-3 py-2 text-left text-sm text-slate-700 last:border-b-0 hover:bg-white"
                        >
                          <span>{item.name}</span>
                          <span className="text-xs text-slate-500">{item.patientId || item.phone || '-'}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {feeRecordModal.selectedPatient ? (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                      <UserRound className="h-3.5 w-3.5" />
                      Selected: {feeRecordModal.selectedPatient.name}
                    </div>
                  ) : null}
                </div>

                <div>
                  <label className="label-modern">Link invoice (optional)</label>
                  <select
                    className="input-modern"
                    value={feeRecordModal.invoiceId}
                    onChange={(event) => setFeeRecordModal((prev) => ({ ...prev, invoiceId: event.target.value }))}
                  >
                    <option value="">No invoice link</option>
                    {invoiceOptionsForFeeModal.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.invoiceNumber} | {item?.Patient?.name || '-'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label-modern">Treatment or service</label>
                  <input
                    className="input-modern"
                    value={feeRecordModal.treatmentNameSnapshot}
                    onChange={(event) =>
                      setFeeRecordModal((prev) => ({ ...prev, treatmentNameSnapshot: event.target.value }))
                    }
                    placeholder="Treatment / Service"
                  />
                </div>

                <div>
                  <label className="label-modern">Consultant fee *</label>
                  <input
                    className="input-modern"
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeRecordModal.agreedConsultantFee}
                    onChange={(event) =>
                      setFeeRecordModal((prev) => ({ ...prev, agreedConsultantFee: event.target.value }))
                    }
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="label-modern">Payment due date</label>
                  <input
                    className="input-modern"
                    type="date"
                    value={feeRecordModal.dueDate}
                    onChange={(event) => setFeeRecordModal((prev) => ({ ...prev, dueDate: event.target.value }))}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="label-modern">Notes</label>
                  <textarea
                    className="textarea-modern"
                    value={feeRecordModal.notes}
                    onChange={(event) => setFeeRecordModal((prev) => ({ ...prev, notes: event.target.value }))}
                    placeholder="Notes"
                  />
                </div>
              </div>
              {feeRecordSaveError ? (
                <div role="alert" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {feeRecordSaveError}
                </div>
              ) : null}
              <div
                data-testid="fee-record-modal-actions"
                className="sticky bottom-0 z-10 -mx-5 mt-5 flex justify-end gap-2 border-t border-slate-100 bg-white px-5 pb-[env(safe-area-inset-bottom)] pt-4 shadow-[0_-10px_24px_rgba(255,255,255,0.96)]"
              >
                <button type="button" className="btn-secondary" onClick={() => setFeeRecordModal((prev) => ({ ...prev, open: false }))}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={saveFeeRecord} disabled={savingAction === 'fee-record'}>
                  {savingAction === 'fee-record' ? 'Saving...' : 'Save Fee Record'}
                </button>
              </div>
            </ModalShell>
          ) : null}

          {paymentModal.open ? (
            <ModalShell title="Add Consultant Payment" onClose={() => setPaymentModal((prev) => ({ ...prev, open: false }))}>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Patient:</span> {paymentModal.record?.Patient?.name || '-'} |{' '}
                  <span className="font-semibold text-slate-900">Consultant:</span> {paymentModal.record?.Consultant?.consultantName || '-'}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Balance Remaining: <span className="font-semibold text-slate-900">{formatCurrency(paymentModal.record?.balanceRemaining)}</span>
                </p>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="label-modern">Amount Paid *</label>
                  <input
                    className="input-modern"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={paymentModal.amountPaid}
                    onChange={(event) => setPaymentModal((prev) => ({ ...prev, amountPaid: event.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="label-modern">Payment Date *</label>
                  <input
                    className="input-modern"
                    type="date"
                    value={paymentModal.paymentDate}
                    onChange={(event) => setPaymentModal((prev) => ({ ...prev, paymentDate: event.target.value }))}
                  />
                </div>
                <div>
                  <label className="label-modern">Payment Mode</label>
                  <input
                    className="input-modern"
                    value={paymentModal.paymentMode}
                    onChange={(event) => setPaymentModal((prev) => ({ ...prev, paymentMode: event.target.value }))}
                    placeholder="Cash / UPI / Transfer / Card"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label-modern">Payment Notes</label>
                  <textarea
                    className="textarea-modern"
                    value={paymentModal.paymentNotes}
                    onChange={(event) => setPaymentModal((prev) => ({ ...prev, paymentNotes: event.target.value }))}
                    placeholder="Payment notes"
                  />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" className="btn-secondary" onClick={() => setPaymentModal((prev) => ({ ...prev, open: false }))}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={savePayment} disabled={savingAction === 'payment'}>
                  {savingAction === 'payment' ? 'Saving...' : 'Add Payment'}
                </button>
              </div>
            </ModalShell>
          ) : null}

          {adjustmentModal.open ? (
            <ModalShell title="Add Consultant Adjustment" onClose={() => setAdjustmentModal((prev) => ({ ...prev, open: false }))}>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Patient:</span> {adjustmentModal.record?.Patient?.name || '-'} |{' '}
                  <span className="font-semibold text-slate-900">Consultant:</span> {adjustmentModal.record?.Consultant?.consultantName || '-'}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Current Balance: <span className="font-semibold text-slate-900">{formatCurrency(adjustmentModal.record?.balanceRemaining)}</span>
                </p>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="label-modern">Adjustment Type *</label>
                  <select
                    className="input-modern"
                    value={adjustmentModal.adjustmentType}
                    onChange={(event) => setAdjustmentModal((prev) => ({ ...prev, adjustmentType: event.target.value }))}
                  >
                    <option value="increase">Increase</option>
                    <option value="decrease">Decrease</option>
                  </select>
                </div>
                <div>
                  <label className="label-modern">Adjustment Amount *</label>
                  <input
                    className="input-modern"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={adjustmentModal.adjustmentAmount}
                    onChange={(event) => setAdjustmentModal((prev) => ({ ...prev, adjustmentAmount: event.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label-modern">Reason *</label>
                  <textarea
                    className="textarea-modern"
                    value={adjustmentModal.reason}
                    onChange={(event) => setAdjustmentModal((prev) => ({ ...prev, reason: event.target.value }))}
                    placeholder="Reason for adjustment"
                  />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" className="btn-secondary" onClick={() => setAdjustmentModal((prev) => ({ ...prev, open: false }))}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={saveAdjustment} disabled={savingAction === 'adjustment'}>
                  {savingAction === 'adjustment' ? 'Saving...' : 'Add Adjustment'}
                </button>
              </div>
            </ModalShell>
          ) : null}
        </div>
      )}
    </FinanceLockGate>
  );
}
